import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUpRight, X } from "lucide-react";

interface GenieImageLightboxProps {
  imageUrl: string;
  title: string;
  /** Page the picture came from, offered as a "View source" link. */
  sourceUrl?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const CONTROL_CLASS =
  "flex min-h-[44px] flex-shrink-0 items-center justify-center gap-1.5 rounded-full text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

/**
 * Full-screen view of a picture: one the Genie shared, or a resource in the
 * dashboard. Sits above every live-agent overlay and nests inside sheets.
 */
export const GenieImageLightbox = ({ imageUrl, title, sourceUrl, isOpen, onClose }: GenieImageLightboxProps) => {
  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[13000] bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[13000] flex flex-col focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 duration-200"
        >
          <div className="flex items-center gap-2 px-4 py-2 sm:px-6">
            <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-sm font-medium text-white">
              {title}
            </DialogPrimitive.Title>
            {sourceUrl && (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className={`${CONTROL_CLASS} px-3`}>
                View source
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
            <DialogPrimitive.Close className={`${CONTROL_CLASS} min-w-[44px]`} aria-label="Close">
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
          <div
            className="flex min-h-0 flex-1 cursor-zoom-out items-center justify-center px-4 pb-4 sm:px-6 sm:pb-6"
            onClick={(event) => event.target === event.currentTarget && onClose()}
          >
            <img
              src={imageUrl}
              alt={title}
              referrerPolicy="no-referrer"
              className="max-h-full max-w-full cursor-default rounded-lg object-contain"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
